/**
 * @file index.tsx
 *
 * @fileoverview Renders appropriate job alert confirmation alert.
 */
import { FormattedMessage } from 'react-intl';
import { Branding } from '~/jobiqo/components/Branding';
import GDPRConsentFormWrapper from '~/jobiqo/components/GDPRConsent/components/FormWrapper';
import { useGdprConsentContext } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import GdprConsentCheckboxes from '~/jobiqo/components/GDPRConsent/components/Checkboxes';
import { User } from '~/jobiqo/models/user';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Modal } from '~/vcl/atoms/15-modal';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { CenterForm } from '~/vcl/organisms/01-form/01-center';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Alert } from '~/vcl/atoms/18-alert';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface JobAlertConfirmProps {
  /**
   * Show alert flag.
   */
  showAlert: boolean;
  /**
   * On close callback.
   */
  onClose: () => void;
  /**
   * On confirm callback.
   */
  onConfirm: () => void;
  /**
   * The flag for determining if the create
   * or delete confirmation should be shown.
   */
  showCreate: boolean;
  /**
   * The current user.
   */
  user: User | null;
}

export const JobAlertConfirm = ({
  showAlert,
  showCreate,
  onClose,
  onConfirm,
  user,
}: JobAlertConfirmProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const { gdprUserConsents } = useGdprConsentContext();

  // In order to show confirm with GDPR consent, we need to
  // make sure that the user has not already given the job alert consent.
  // ShowAlert and showCreate also has to be set.
  const showConfirmWithConsent =
    gdprUserConsents.length === 0 && showCreate && showAlert;

  if (showConfirmWithConsent && user) {
    const {
      consentsValidation,
      createGdprUserConsent,
    } = useGdprConsentContext();
    const methods = useForm({
      mode: 'onChange',
      defaultValues: {
        ...consentsValidation.initialValues,
      },
      resolver: yupResolver(Yup.object().shape(consentsValidation.schema)),
    });

    const {
      handleSubmit,
      formState: { isSubmitting, isValid },
    } = methods;

    return (
      <FormProvider {...methods}>
        <Modal
          isShown={showConfirmWithConsent}
          maxWidth="550px"
          padding={[7, 11]}
          onHide={onClose}
        >
          <AlignedCenter>
            <Branding maxHeight={frontendTheme.logo.singlePageMaxHeight} />
            <Spacing />
            <GDPRConsentFormWrapper consentPlacement="JOB_ALERT">
              <CenterForm
                onSubmit={handleSubmit(() => {
                  createGdprUserConsent(user.email);
                  onConfirm();
                })}
              >
                <GdprConsentCheckboxes />
                <Spacing size="medium" />
                <FlexItems
                  flexDirection={['column', 'row']}
                  justifyContent="center"
                  gap={8}
                >
                  <FlexItem>
                    <Button
                      onClick={onClose}
                      buttonStyle="danger"
                      raised={false}
                      block={true}
                    >
                      <FormattedMessage id="cancel" defaultMessage="Cancel" />
                    </Button>
                  </FlexItem>
                  <FlexItem>
                    <Button
                      disabled={isSubmitting || !isValid}
                      block={true}
                      type="submit"
                      buttonStyle="primary"
                      data-test-id="create_job_alert_btn"
                    >
                      <FormattedMessage
                        id="create_job_alert"
                        defaultMessage="Create Job Alert"
                      />
                    </Button>
                  </FlexItem>
                </FlexItems>
              </CenterForm>
            </GDPRConsentFormWrapper>
          </AlignedCenter>
        </Modal>
      </FormProvider>
    );
  }

  return showCreate ? (
    <Alert
      isShown={showAlert}
      onConfirm={onConfirm}
      onClose={onClose}
      confirmText={
        <div data-test-id="job_alert_confirm_create">
          <FormattedMessage
            id="job_alert_confirm_create"
            defaultMessage="Yes, create"
          />
        </div>
      }
      cancelText={<FormattedMessage id="cancel" defaultMessage="Cancel" />}
      alertLabel={
        <FormattedMessage
          id="job_alert_confirm_create_label"
          defaultMessage="Create"
        />
      }
    >
      <div data-test-id="job_alert_confirm_create_text">
        <FormattedMessage
          id="job_alert_confirm_create_text"
          defaultMessage="Are you sure you want to create job alert?"
        />
      </div>
    </Alert>
  ) : (
    <Alert
      isShown={showAlert}
      onConfirm={onConfirm}
      onClose={onClose}
      confirmText={
        <FormattedMessage
          id="job_alert_confirm_delete"
          defaultMessage="Yes, delete"
        />
      }
      cancelText={<FormattedMessage id="cancel" defaultMessage="Cancel" />}
      alertLabel={
        <FormattedMessage
          id="job_alert_confirm_delete_label"
          defaultMessage="Delete"
        />
      }
    >
      <FormattedMessage
        id="job_alert_confirm_delete_text"
        defaultMessage="Are you sure you want to delete job alert?"
      />
    </Alert>
  );
};
export default JobAlertConfirm;
